import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Chip, Stack, Typography } from "@mui/material";
import { format, isSameDay, isToday } from "date-fns";
import { styled } from "@mui/styles";
import { getDateFnsLocale } from "utils/getDateFnsLocale";

const DateItem = styled(Stack)(({ theme }) => ({
  padding: "12px 0",
  borderRadius: "6px",
  alignItems: "center",
  width: "65px",
  flexShrink: 0,
  cursor: "pointer",
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.action.disabledBackground,
  "&:hover": {
    backgroundColor: "#FAF7FD",
    color: theme.palette.primary.main,
  },
  "&.selected": {
    backgroundColor: "#FAF7FD",
    border: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
  },
  "&.disabled": {
    color: theme.palette.text.disabled,
    pointerEvents: "none",
  },
}));

export default function DateRowSelect({
  startDate,
  endDate,
  selectedDate,
  setSelectedDate,
  disabledDates = [],
  fullDates = [],
}) {
  const { t } = useTranslation(["rescheduling"]);
  const scrollContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Drag state variables
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Generate dates between startDate and endDate (inclusive)
  const getDatesInRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const lastDate = new Date(end);

    while (currentDate <= lastDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  // Get array of dates in the range
  const dateRange = getDatesInRange(startDate, endDate);

  const handleDragStart = (clientX) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(clientX);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleDragMove = (clientX) => {
    if (!isDragging || !scrollContainerRef.current) return;
    const x = clientX;
    const walk = (startX - x) * 2; // Multiply by 2 for faster drag response
    scrollContainerRef.current.scrollLeft = scrollLeft + walk;
  };

  useEffect(() => {
    if (selectedDate)
      setSelectedIndex(dateRange.findIndex((date) => isSameDay(date, new Date(selectedDate))));
  }, []);

  useEffect(() => {
    // Update container width
    if (scrollContainerRef.current) {
      setContainerWidth(scrollContainerRef.current.offsetWidth);
    }
  }, [selectedDate, dateRange]);

  // Center the selected date
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const [itemWidth, itemGap] = [65, 8];
      const scrollPosition = selectedIndex * (itemWidth + itemGap);
      container.scrollLeft = scrollPosition > 0 ? scrollPosition : 0;
    }
  }, [selectedIndex, containerWidth]);

  return (
    <Stack
      id="date-row-select"
      direction="row"
      gap={3}
      sx={{
        overflow: "auto",
        scrollBehavior: isDragging ? "auto" : "smooth", // Disable smooth scrolling during drag
        width: "100%",
        // Add padding on both sides to allow centering first and last items
        px: containerWidth > 0 ? `${Math.max(containerWidth / 2 - 65 / 2, 0)}px` : 0,
        cursor: isDragging ? "grabbing" : "grab", // Change cursor based on drag state
        userSelect: "none", // Prevent text selection during drag
        // Hide scrollbar
        "&::-webkit-scrollbar": { display: "none" }, // Chrome, Safari, Edge
        scrollbarWidth: "none", // Firefox
      }}
      ref={scrollContainerRef}
      onMouseDown={(e) => handleDragStart(e.clientX)}
      onMouseMove={(e) => handleDragMove(e.clientX)}
      onMouseUp={() => setIsDragging(false)}
      onMouseLeave={() => isDragging && setIsDragging(false)}
    >
      {dateRange.map((date, index) => {
        const isSelected = isSameDay(new Date(selectedDate), date);
        const isDisabled =
          disabledDates?.some((disabledDate) => isSameDay(new Date(disabledDate), date)) || false;
        const isFull = fullDates?.some((fullDate) => isSameDay(new Date(fullDate), date)) || false;
        return (
          <DateItem
            key={index}
            className={`${isSelected ? "selected " : ""} ${isDisabled || isFull ? "disabled " : ""}`}
            onClick={() => {
              setSelectedDate(date.toISOString());
              setSelectedIndex(index);
            }}
          >
            <Typography variant="body2">
              {isToday(date) ? t("rescheduling:today") : format(date, "EEE", { locale: getDateFnsLocale() })}
            </Typography>
            {isFull ? (
              <Chip label={t("rescheduling:full")} size="small" color="warning" />
            ) : (
              <Typography variant="body2">{date.getDate()}</Typography>
            )}
          </DateItem>
        );
      })}
    </Stack>
  );
}
